import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, CheckCircle, SignIn, SignOut, Buildings, Warning, LockKey, Backspace, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIOSK_FIRMA_KEY = "kiosk_firma";

function FirmaKeuze({ onKies }) {
  const [firmas, setFirmas] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/firmas`).then((r) => setFirmas(r.data)).catch(() => setFirmas([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-4" data-testid="kiosk-firma-keuze">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Kies je firma</h1>
        <p className="text-sm text-muted-foreground mb-5">Deze keuze wordt op dit toestel bewaard.</p>
        <div className="space-y-2">
          {firmas.map((f) => (
            <button
              key={f.id}
              onClick={() => onKies(f.id)}
              className="w-full bg-white border border-border rounded-sm p-4 flex items-center gap-3 hover:border-[#002FA7] transition-colors text-left"
              data-testid={`kiosk-kies-firma-${f.id}`}
            >
              <Buildings size={20} className="text-[#002FA7]" />
              <span className="font-bold">{f.naam}</span>
            </button>
          ))}
          {firmas.length === 0 && <p className="text-sm text-muted-foreground">Geen firma's beschikbaar.</p>}
        </div>
      </div>
    </div>
  );
}

export default function Inklokken() {
  const { filiaalId } = useParams();
  const [firmaId, setFirmaId] = useState(() => {
    const uit_url = new URLSearchParams(window.location.search).get("firma");
    if (uit_url) localStorage.setItem(KIOSK_FIRMA_KEY, uit_url);
    return uit_url || localStorage.getItem(KIOSK_FIRMA_KEY) || "";
  });
  const [filialen, setFilialen] = useState([]);
  const [selectedFiliaal, setSelectedFiliaal] = useState(filiaalId || "");
  const [pincode, setPincode] = useState("");
  const [identified, setIdentified] = useState(null);
  const [pinFout, setPinFout] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [aanwezigVandaag, setAanwezigVandaag] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filiaalInfo, setFiliaalInfo] = useState(null);
  const [firmaZoeken, setFirmaZoeken] = useState(!!filiaalId);

  // QR van een filiaal: firma rechtstreeks opzoeken, geen firmakeuze nodig
  useEffect(() => {
    if (!filiaalId) { setFirmaZoeken(false); return; }
    let actief = true;
    axios
      .get(`${API}/public/filiaal/${filiaalId}`)
      .then((r) => {
        if (!actief) return;
        setFiliaalInfo(r.data);
        if (r.data?.firma_id) {
          localStorage.setItem(KIOSK_FIRMA_KEY, r.data.firma_id);
          setFirmaId(r.data.firma_id);
        }
      })
      .catch(() => {})
      .finally(() => { if (actief) setFirmaZoeken(false); });
    return () => { actief = false; };
  }, [filiaalId]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!firmaId) { setLoading(false); return; }
    const cfg = { headers: { "X-Firma-Id": firmaId } };
    const fetchData = async () => {
      try {
        const [filRes, aanwRes] = await Promise.all([
          axios.get(`${API}/public/filialen?firma_id=${firmaId}`),
          axios.get(`${API}/aanwezigheid/vandaag`, cfg),
        ]);
        setFilialen(filRes.data);
        setAanwezigVandaag(aanwRes.data);
        if (filiaalId) setSelectedFiliaal(filiaalId);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [filiaalId, firmaId]);

  // Pincodes zijn 4 tot 6 cijfers: pas opzoeken als er even niet meer getypt wordt
  useEffect(() => {
    setPinFout(false);
    if (pincode.length < 4) { setIdentified(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/pin/lookup`, { pincode }, { headers: { "X-Firma-Id": firmaId } });
        setIdentified(res.data);
        setPinFout(false);
        setResult(null);
        if (!filiaalId && res.data.filiaal_ids?.length > 0) setSelectedFiliaal(res.data.filiaal_ids[0]);
      } catch (e) {
        setIdentified(null);
        setPinFout(true);
      }
    }, pincode.length >= 6 ? 150 : 900);
    return () => clearTimeout(timer);
  }, [pincode, filiaalId, firmaId]);

  const isIngeklokt = identified ? aanwezigVandaag.find((a) => a.medewerker_id === identified.medewerker_id && !a.uitklok_tijd) : null;

  const handleInklokken = async () => {
    if (!pincode || !selectedFiliaal) return;
    setProcessing(true);
    const cfg = { headers: { "X-Firma-Id": firmaId } };
    try {
      const res = await axios.post(`${API}/pin/inklokken`, { pincode, filiaal_id: selectedFiliaal }, cfg);
      setResult({ type: "inklok", ...res.data });
      toast.success(`${res.data.naam} ingeklokt!`);
      const aanwRes = await axios.get(`${API}/aanwezigheid/vandaag`, cfg);
      setAanwezigVandaag(aanwRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setProcessing(false); }
  };

  const handleUitklokken = async () => {
    if (!pincode) return;
    setProcessing(true);
    const cfg = { headers: { "X-Firma-Id": firmaId } };
    try {
      const res = await axios.post(`${API}/pin/uitklokken`, { pincode }, cfg);
      setResult({ type: "uitklok", ...res.data });
      toast.success(`${res.data.naam} uitgeklokt!`);
      const aanwRes = await axios.get(`${API}/aanwezigheid/vandaag`, cfg);
      setAanwezigVandaag(aanwRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setProcessing(false); }
  };

  const pad = (n) => { if (pincode.length < 6) setPincode((p) => p + n); };
  const back = () => setPincode((p) => p.slice(0, -1));
  const clear = () => { setPincode(""); setIdentified(null); setPinFout(false); setResult(null); };

  const formatTime = (d) => d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const isLaat = () => { const n = new Date(); return n.getHours() > 10 || (n.getHours() === 10 && n.getMinutes() > 0); };
  const selectedFilObj = filialen.find((f) => f.id === selectedFiliaal);

  if (firmaZoeken) {
    return <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-lg">Filiaal laden...</div></div>;
  }

  if (!firmaId) {
    return <FirmaKeuze onKies={(id) => { localStorage.setItem(KIOSK_FIRMA_KEY, id); setFirmaId(id); }} />;
  }

  if (loading) return <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-lg">Laden...</div></div>;

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex flex-col" data-testid="inklokken-page">
      {/* Header */}
      <header className="bg-[#002FA7] text-white px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-base font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="kiosk-firma-naam">
            {filiaalInfo?.firma_naam || "Personeel Register"}
          </h1>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-70" data-testid="kiosk-filiaal-naam">
            {filiaalInfo?.naam || selectedFilObj?.naam || "Inklokken"}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-black tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{formatTime(currentTime)}</p>
          <p className="text-[10px] opacity-70">{currentTime.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center p-3 sm:p-4 max-w-lg mx-auto w-full">

        {/* Te laat */}
        {isLaat() && (
          <div className="w-full bg-[#E53E3E] text-white rounded-sm p-3 mb-3 flex items-center gap-2" data-testid="late-warning">
            <Warning size={20} weight="bold" className="shrink-0" />
            <p className="text-sm font-bold">Na 10:00 — wordt als te laat geregistreerd</p>
          </div>
        )}

        {/* Filiaal */}
        <div className="w-full bg-white border border-border rounded-sm p-3 mb-3">
          {filiaalId ? (
            <div className="flex items-center gap-2 h-12 px-3 rounded-sm bg-[#002FA7]/5 border border-[#002FA7]/20" data-testid="filiaal-vast">
              <MapPin size={20} weight="fill" className="text-[#002FA7] shrink-0" />
              <div className="min-w-0">
                <p className="text-base font-bold truncate">{filiaalInfo?.naam || selectedFilObj?.naam || "Filiaal"}</p>
                <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Vast filiaal via QR-code</p>
              </div>
            </div>
          ) : (
          <Select value={selectedFiliaal} onValueChange={setSelectedFiliaal}>
            <SelectTrigger className="rounded-sm h-12 text-base font-medium" data-testid="select-filiaal">
              <SelectValue placeholder="Selecteer filiaal..." />
            </SelectTrigger>
            <SelectContent>
              {filialen.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`option-filiaal-${f.id}`} className="text-base py-3">
                  {f.naam}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>

        {/* Identified user */}
        {identified && (
          <div className="w-full bg-[#002FA7] text-white rounded-sm p-4 mb-3 flex items-center gap-3" data-testid="identified-user">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-black text-lg">
              {identified.voornaam?.[0]}{identified.achternaam?.[0]}
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg">{identified.naam}</p>
              <p className="text-xs opacity-70">{identified.functie || "Medewerker"}</p>
            </div>
            {isIngeklokt && (
              <span className="text-xs font-bold bg-[#38A169] px-3 py-1 rounded-sm">INGEKLOKT</span>
            )}
          </div>
        )}

        {pinFout && !identified && (
          <div className="w-full bg-[#E53E3E] text-white rounded-sm p-3 mb-3 text-center" data-testid="pin-invalid">
            <p className="font-bold">Ongeldige pincode</p>
          </div>
        )}

        {/* PIN + Numpad */}
        <div className="w-full bg-white border border-border rounded-sm p-4 mb-3" data-testid="pin-entry">
          {/* PIN dots */}
          <div className="flex justify-center gap-3 mb-4" data-testid="pin-dots">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`w-5 h-5 rounded-full border-2 transition-all duration-150 ${i < pincode.length ? "bg-[#002FA7] border-[#002FA7] scale-125" : "bg-transparent border-gray-300"}`} />
            ))}
          </div>

          {/* Numpad - GROOT voor touchscreen */}
          <div className="grid grid-cols-3 gap-2" data-testid="numpad">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button key={n} onClick={() => pad(String(n))} className="h-[70px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-[#002FA7] active:text-white text-2xl font-bold transition-colors select-none" data-testid={`numpad-${n}`}>
                {n}
              </button>
            ))}
            <button onClick={clear} className="h-[70px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-[#E53E3E] active:text-white text-base font-bold transition-colors select-none" data-testid="numpad-clear">Wis</button>
            <button onClick={() => pad("0")} className="h-[70px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-[#002FA7] active:text-white text-2xl font-bold transition-colors select-none" data-testid="numpad-0">0</button>
            <button onClick={back} className="h-[70px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-gray-400 flex items-center justify-center transition-colors select-none" data-testid="numpad-backspace"><Backspace size={28} weight="bold" /></button>
          </div>
        </div>

        {/* Inklokken / Uitklokken - GROTE knoppen */}
        <div className="w-full grid grid-cols-2 gap-3 mb-3">
          <button
            onClick={handleInklokken}
            disabled={processing || !identified || !selectedFiliaal || !!isIngeklokt}
            className="h-16 rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors select-none active:scale-[0.98]"
            data-testid="inklokken-btn"
          >
            <SignIn size={24} weight="bold" /> Inklokken
          </button>
          <button
            onClick={handleUitklokken}
            disabled={processing || !identified || !isIngeklokt}
            className="h-16 rounded-sm bg-white border-2 border-[#002FA7] text-[#002FA7] font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors select-none active:scale-[0.98] active:bg-[#002FA7]/10"
            data-testid="uitklokken-btn"
          >
            <SignOut size={24} weight="bold" /> Uitklokken
          </button>
        </div>

        {/* Result */}
        {result && (
          <div className={`w-full rounded-sm p-4 mb-3 ${result.type === "inklok" ? result.te_laat ? "bg-[#E53E3E] text-white" : "bg-[#38A169] text-white" : "bg-gray-100"}`} data-testid="klok-result">
            <div className="flex items-center gap-2">
              {result.type === "inklok" ? (
                result.te_laat ? <Warning size={22} weight="bold" /> : <CheckCircle size={22} weight="bold" />
              ) : <SignOut size={22} weight="bold" />}
              <p className="font-bold text-base">{result.message}</p>
            </div>
            {result.te_laat && result.type === "inklok" && <p className="text-sm mt-1 opacity-80">Admin is op de hoogte gesteld</p>}
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground" data-testid="werkdag-info">Werkdag 10:00 — 18:00 | Voer pincode in om in/uit te klokken</p>
        {!filiaalId && (
          <button
            onClick={() => { localStorage.removeItem(KIOSK_FIRMA_KEY); setFirmaId(""); }}
            className="text-xs text-muted-foreground underline mt-2"
            data-testid="kiosk-wijzig-firma-btn"
          >
            Andere firma kiezen
          </button>
        )}
      </main>
    </div>
  );
}
